import { assertNever } from "@ondo/shared";
import { useParams } from "react-router";

import { LoadingScreen } from "../lib/Loading.tsx";
import { RoomFrame, RoomGate, RoomWorkCard, RoomWorkPanel } from "./RoomParts.tsx";
import { useRoomController } from "./useRoomController.ts";
// Imported for `WorkIdentity`, which this screen reuses. The alternative was
// restating .work__title, .work__attribution and .work__facts under a room
// prefix, which is two definitions of one block and the beginning of a drift.
import "../work/work.css";
import "./room.css";

/**
 * A viewing room, for whoever was sent the link.
 *
 * The only address in ONDO that does not ask who is reading it, and the whole
 * of what it can disclose was decided by `ondo.viewing_room_contents()` before
 * anything here ran. Nothing on this screen filters, hides or omits: what it is
 * given is what the room's owner chose to publish.
 */
export function ViewingRoom() {
  const { handle, slug } = useParams();
  const controller = useRoomController(handle, slug);
  const { view } = controller;

  switch (view.kind) {
    case "loading":
      return (
        <RoomFrame head={null}>
          <LoadingScreen message={view.message} />
        </RoomFrame>
      );

    case "missing":
      return (
        <RoomFrame head={null}>
          <section className="room__missing">
            <h1 className="room__title">{view.title}</h1>
            <p className="room__note">{view.message}</p>
          </section>
        </RoomFrame>
      );

    case "refused":
      return (
        <RoomFrame head={view.head}>
          <section className="room__missing">
            <h2 className="room__gate-title">{view.title}</h2>
            <p className="room__note" role="alert">
              {view.message}
            </p>
          </section>
        </RoomFrame>
      );

    case "needs-email":
      return (
        <RoomFrame head={view.head}>
          <RoomGate
            lead={view.lead}
            label={view.label}
            action={view.action}
            value={view.value}
            problem={view.problem}
            busy={view.busy}
            secret={false}
            onType={controller.typeEmail}
            onSubmit={controller.enter}
          />
        </RoomFrame>
      );

    case "needs-password":
      return (
        <RoomFrame head={view.head}>
          <RoomGate
            lead={view.lead}
            label={view.label}
            action={view.action}
            value={view.value}
            problem={view.problem}
            busy={view.busy}
            secret
            onType={controller.typePassword}
            onSubmit={controller.enter}
          />
        </RoomFrame>
      );

    case "showing":
      return (
        <RoomFrame head={view.head}>
          <ul className="room__grid">
            {view.cards.map((card) => (
              <RoomWorkCard
                key={card.id}
                card={card}
                onOpen={() => controller.openWork(card.id)}
              />
            ))}
          </ul>

          <p className="room__note">{view.note}</p>
          <p className="room__footnote">{view.footnote}</p>
        </RoomFrame>
      );

    case "work":
      return (
        <RoomFrame head={view.head}>
          <RoomWorkPanel
            work={view.work}
            backLabel={view.backLabel}
            onBack={controller.closeWork}
          />
          <p className="room__footnote">{view.footnote}</p>
        </RoomFrame>
      );

    default:
      return assertNever(view);
  }
}
